<template>
  <div :class="containerClasses">
    <div v-if="showCard">
      <v-card
        outlined
        class="card-content"
        @mouseover="hover = true" @mouseleave="hover = false"
      >
        <CardContentLargeSize
          v-if="showLargeContent"
          @onRedirectionToAnalysis="onRedirectionToAnalysis"
          @onRedirectionToSourceDocument="onRedirectionToSourceDocument"
          :document-details="augmentedDocumentData.documentDetails"
          :show-description="showDescription"
        />
        <CardContentMediumSize
          v-if="showMediumContent"
          @onRedirectionToAnalysis="onRedirectionToAnalysis"
          @onRedirectionToSourceDocument="onRedirectionToSourceDocument"
          :document-details="augmentedDocumentData.documentDetails"
        />
        <CardContentSmallSize
          v-if="showSmallContent"
          @onRedirectionToAnalysis="onRedirectionToAnalysis"
          @onRedirectionToSourceDocument="onRedirectionToSourceDocument"
          :document-details="augmentedDocumentData.documentDetails"
          :show-description="showDescription"
        />
        <HorizontalCardBottom :small="small">
          <template slot="left">
            <RecommendationRating
              v-if="showEvaluation && !isAnalysis"
              :recommendation="recommendation"
            />
            <TagsListHorizontal v-if="!showAvatar" :tags="tags" class="pl-2 flex-grow-1" />
            <div
              v-if="showAvatar"
              class="avatar-list"
            >
              <ACLAvatarList
                class="d-flex flex-direction-row"
                :acl="augmentedDocumentData.documentDetails.acl"
                :before-ellipsis="3"
              />
            </div>
          </template>
          <template slot="middle">
            <div v-if="showSmallContent && hover" class="d-flex align-center">
              <v-icon v-if="!showDescription" @click.exact="onShowDescription">
                keyboard_arrow_down
              </v-icon>
              <v-icon v-if="showDescription" @click.exact="onShowDescription">
                keyboard_arrow_up
              </v-icon>
            </div>
          </template>
          <template slot="right">
            <div class="section-actions">
              <div v-if="isAnalysis" class="analysis-section d-flex align-center">
                <span class="action pa-1">
                  <v-icon v-if="hasDocumentScore" x-small>
                    fa-gem
                  </v-icon>
                  <v-icon v-else x-small style="color: #ddd">
                    fa-gem
                  </v-icon>
                  <span>
                    {{ documentScore }}
                  </span>
                </span>
                <span class="action pa-1">
                  <v-icon x-small>
                    fa-comments
                  </v-icon>
                  <span>
                    {{ totalUserActions }}
                  </span>
                </span>
              </div>
              <HorizontalCardMenu
                class="action"
                :document="augmentedDocumentData"
                :allow-adding-to-reflection="allowAddingToReflection"
                @similarDocuments="generateMoreLikeThis"
                @addTo="onAddTo"
                :disable-add-to="!isAnalysis"
              />
            </div>
          </template>
        </HorizontalCardBottom>
      </v-card>
    </div>
    <div v-if="!showCard && !moreLikeThisShow" class="skeleton-loader" :class="{small, medium, large}">
      <v-skeleton-loader
        height="100%"
        type="card"
      />
    </div>
    <MoreLikeThis
      v-if="augmentedDocumentData"
      ref="moreLikeThis"
      @close="moreLikeThisShow = !moreLikeThisShow"
      v-show="moreLikeThisShow && !isLoading"
      :document-details="augmentedDocumentData.documentDetails"
    >
      <template v-slot:item="{ item }">
        <HorizontalCard
          @clicked="sendEvent('clicked', $event);"
          @clicked-source="sendEvent('clicked-source', $event);"
          :show-evaluation="false"
          :document-data="item"
          :allow-adding-to-reflection="allowAddingToReflection"
          :event-bus="eventBus"
          :full-width="fullWidth"
          :medium="medium"
          :small="small"
          :large="large"
        />
      </template>
    </MoreLikeThis>
    <SendAnalysis
      v-if="analysis"
      :value="showSendAnalysis"
      :analysis="analysis"
      @close="showSendAnalysis = false"
      @cancel="showSendAnalysis = false"
    />
  </div>
</template>

<script>
import { addAnalysisMetadataOnDocument } from '@/shared/services/KitapoService';
import ACLAvatarList from '@/shared/components/avatar/ACLAvatarList';
import TagsListHorizontal from '@/components/tags/TagsListHorizontal';
import SendAnalysis from '@/shared/components/dialog/SendAnalysis';
import RecommendationRating from '../../gems/RecommendationRating';
import MoreLikeThis from './MoreLikeThis';
import CardContentLargeSize from './CardContentLargeSize';
import CardContentMediumSize from './CardContentMediumSize';
import CardContentSmallSize from './CardContentSmallSize';
import HorizontalCardBottom from './HorizontalCardBottom';
import HorizontalCardMenu from './HorizontalCardMenu';

export default {
  name: 'HorizontalCard',
  components: {
    RecommendationRating,
    MoreLikeThis,
    CardContentLargeSize,
    CardContentMediumSize,
    CardContentSmallSize,
    ACLAvatarList,
    TagsListHorizontal,
    HorizontalCardBottom,
    HorizontalCardMenu,
    SendAnalysis,
  },
  data() {
    return {
      savedToPortfolio: true,
      openConfirmationDialog: false,
      hover: false,
      moreLikeThisShow: false,
      showDescription: false,
      showSendAnalysis: false,
      analysis: undefined,
      augmentedDocumentData: undefined,
    };
  },
  props: {
    eventBus: {
      type: Object,
      default: undefined,
    },
    documentData: {
      type: Object,
      required: true,
    },
    showEvaluation: {
      type: Boolean,
      default: true,
    },
    allowAddingToReflection: {
      type: Boolean,
      required: true,
    },
    target: {
      type: String,
      default: '',
    },
    showMoreLikeThis: {
      type: Boolean,
      default: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    medium: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    showAvatar: {
      type: Boolean,
      default: false,
    },
    showTags: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    showCard() {
      return !this.moreLikeThisShow && !this.isLoading && this.augmentedDocumentData;
    },
    showLargeContent() {
      if (!this.medium && !this.small && !this.large) return false;

      return this.large;
    },
    showMediumContent() {
      if (!this.medium && !this.small && !this.large) return true;

      return this.medium;
    },
    showSmallContent() {
      if (!this.medium && !this.small && !this.large) return false;

      return this.small;
    },
    documentExternalLink() {
      return this.augmentedDocumentData.documentDetails.url;
    },
    totalUserActions() {
      return this.augmentedDocumentData.documentDetails.activityCount || 0;
    },
    isAnalysis() {
      return this.augmentedDocumentData.documentDetails.analysisId;
    },
    hasDocumentScore() {
      return Number.isFinite(this.augmentedDocumentData.documentDetails.documentScore);
    },
    tags() {
      return this.augmentedDocumentData.documentDetails.tags;
    },
    documentScore() {
      const { documentScore } = this.augmentedDocumentData.documentDetails;
      if (!this.hasDocumentScore) {
        return ' - ';
      }
      return (documentScore / 20).toFixed(1);
    },
    recommendation() {
      return this.augmentedDocumentData.documentDetails.recommendation;
    },
    containerClasses() {
      if (!this.moreLikeThisShow) {
        if (this.fullWidth) return 'small-padding-y small-padding-x col-12';

        // When SM there is no left section tree, so 2 cards per row is fine.
        return 'pa-2 col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3';
      }

      return 'col-12';
    },
    showActions() {
      if (this.showMediumContent) return true;

      return this.showDescription;
    },
  },
  async created() {
    this.augmentedDocumentData = await addAnalysisMetadataOnDocument(this.documentData);
    this.updateAnalysis();
  },
  methods: {
    onAddTo() {
      if (!this.augmentedDocumentData.documentDetails.acl) {
        console.error('No ACL on document, can\'t share', this.augmentedDocumentData.documentDetails);
        return;
      }

      this.showSendAnalysis = true;
    },
    updateAnalysis() {
      let id = this.augmentedDocumentData.analysisId;

      if (!id) {
        id = this.augmentedDocumentData.documentDetails.analysisId;
      }
      if (!id) {
        // eslint-disable-next-line
        id = this.augmentedDocumentData.documentDetails.id;
      }

      this.analysis = {
        ...this.augmentedDocumentData.documentDetails,
        documentDetails: { ...this.augmentedDocumentData.documentDetails },
        id,
        analysisId: id,
      };
    },
    analysisLink() {
      if (this.augmentedDocumentData.documentDetails.isUpload) {
        let docId = this.augmentedDocumentData.documentDetails.id;
        if (!docId) {
          docId = this.augmentedDocumentData.documentDetails.documentId;
        }
        if (!docId) {
          docId = this.augmentedDocumentData.documentId;
        }
        return `/doc/${docId}/${this.analysis.id}`;
      }
      const { url } = this.augmentedDocumentData.documentDetails;
      const getUrl = window.location;
      const baseUrl = `${getUrl.protocol}//${getUrl.host}/redirect`;
      const targetUrl = new URL(baseUrl);
      targetUrl.searchParams.append('redirectUrl', url);

      if (this.augmentedDocumentData.documentDetails.analysisId) {
        targetUrl.searchParams.append('analysisId', this.augmentedDocumentData.documentDetails.analysisId);
      }

      return `${targetUrl.pathname}${targetUrl.search}`;
    },
    onShowDescription() {
      this.showDescription = !this.showDescription;
    },
    onRedirectionToAnalysis() {
      this.sendEvent('clicked', this.augmentedDocumentData);
      window.open(this.analysisLink(), '_blank');
    },
    onRedirectionToSourceDocument() {
      this.sendEvent('clicked-source', this.augmentedDocumentData);
      window.open(this.documentExternalLink, '_blank');
    },
    generateMoreLikeThis() {
      this.moreLikeThisShow = true;
      const { moreLikeThis } = this.$refs;

      moreLikeThis.generateMoreLikeThis();
    },
    sendEvent(event, augmentedDocumentData) {
      console.log('Send event', event, augmentedDocumentData);
      this.$emit(event, augmentedDocumentData);

      if (this.eventBus) {
        console.log('Send event to event bus', event, augmentedDocumentData);
        this.eventBus.$emit(event, augmentedDocumentData);
      }
    },
  },
  watch: {
    async documentData(newValue) {
      this.augmentedDocumentData = await addAnalysisMetadataOnDocument(newValue);
      this.updateAnalysis();
      this.moreLikeThisShow = false;
    },
  },
};
</script>

<style scoped>
.card-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 0px solid black;
  border-radius: 8px;
  overflow: hidden;
}
.more-like-this-btn {
  font-size: 12px;
}
.action {
  margin-left: 4px;
  margin-right: 4px;
}
.section-actions {
  display: flex;
  flex-shrink: 0;
}
.analysis-section {
  font-size: 14px;
  color: rgb(60, 60, 60);
  font-weight: 400;
}
.small-padding-x {
  padding-left: 12px;
  padding-right: 12px;
}
.small-padding-y {
  padding-top: 6px;
  padding-bottom: 6px;
}

.avatar-list {
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
}

.skeleton-loader.small {
  height: 120px;
}

.skeleton-loader.medium {
  height: 258px;
}

.skeleton-loader.large {
  height: 336px;
}

::v-deep .v-skeleton-loader > * {
  height: 100%;
  display: flex;
  flex-direction: column;
}

::v-deep .v-skeleton-loader .v-skeleton-loader__bone {
  flex-grow: 1;
}
</style>
